/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { usePageContentId } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import type {
	useAllowNestedCommentsDetailsPanelContentCreatedDateQuery as useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$data,
	useAllowNestedCommentsDetailsPanelContentCreatedDateQueryVariables as useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$variables,
} from './__types__/useAllowNestedCommentsDetailsPanelContentCreatedDateQuery';

const useAllowNestedCommentsDetailsPanelContentCreatedDateQuery = graphql`
	query useAllowNestedCommentsDetailsPanelContentCreatedDateQuery(
		$contentId: ID!
		$status: String!
	) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				metadata {
					createdDate
				}
			}
		}
	}
`;
const NESTED_COMMENTS_LAUNCH_DATE = new Date(Date.UTC(2025, 2, 6)); // 6th March 2025. Only month is 0 indexed.

// NOTE: This hook is ONLY used for the legacy footer (i.e. that huge, old, js package)
// The new footer relies on state within useCommentsPanel
// This code should be deprecated alongside the legacy footer
export const useAllowNestedComments = () => {
	const [contentId] = usePageContentId();
	const pageMode = useGetPageMode();

	const {
		data: createdDateData,
		loading,
		error,
	} = useQuery<
		useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$data,
		useAllowNestedCommentsDetailsPanelContentCreatedDateQuery$variables
	>(useAllowNestedCommentsDetailsPanelContentCreatedDateQuery, {
		variables: {
			contentId: contentId ?? '',
			status: 'current',
		},
		skip: !contentId,
	});
	// If the feature flag is not enabled, we should always allow nested comments
	if (!fg('confluence_frontend_comments_panel_v2')) {
		return true;
	}

	const pageCreatedDate = createdDateData?.content?.nodes?.[0]?.metadata.createdDate;

	const isLivePage = pageMode === PageMode.LIVE;

	if (loading || error || !pageCreatedDate) {
		return false;
	}

	const pageCreateDateObject = new Date(pageCreatedDate);

	const isBeforeLaunchDate = pageCreateDateObject < NESTED_COMMENTS_LAUNCH_DATE;

	return isBeforeLaunchDate && !isLivePage;
};
